/**
 * Email Collection Hook with Customer.io Integration
 * Handles all email collection forms with deduplication and analytics
 */

import { useState, useCallback } from 'react';
import { customerIO } from '../services/customerio';
import { useEventDeduplication } from './useEventDeduplication';
import { useAnalytics } from './useAnalytics';
import { useAuth } from '../context/AuthContext';

export interface EmailCollectionOptions {
  source: string; // e.g., 'audio_memories_page', 'newsletter_signup', 'image_memories_page'
  feature?: string; // e.g., 'early_access', 'newsletter', 'enterprise_demo'
  successMessage?: string;
  errorMessage?: string;
  additionalTraits?: Record<string, any>;
  trackAnalytics?: boolean; // Default: true
}

export interface EmailCollectionState {
  isSubmitting: boolean;
  isSuccess: boolean;
  error: string | null;
}

export function useEmailCollection(options: EmailCollectionOptions) {
  const [state, setState] = useState<EmailCollectionState>({
    isSubmitting: false,
    isSuccess: false,
    error: null,
  });

  const { shouldPreventDuplicateEvent } = useEventDeduplication(5000); // 5 second window
  const { trackEvent } = useAnalytics();
  const { user } = useAuth();

  const submitEmail = useCallback(async (email: string): Promise<boolean> => {
    // Input validation
    if (!email || !email.includes('@')) {
      setState(prev => ({ 
        ...prev, 
        error: 'Please enter a valid email address',
        isSuccess: false 
      }));
      return false;
    }

    // Check for duplicate events
    const eventKey = {
      eventName: 'email_collection',
      userId: user?.id || 'anonymous',
      additionalKey: `${options.source}_${email}`
    };

    if (shouldPreventDuplicateEvent(eventKey)) {
      setState(prev => ({ 
        ...prev, 
        error: 'Email already submitted recently. Please wait a moment.',
        isSuccess: false 
      }));
      return false;
    }

    setState(prev => ({ 
      ...prev, 
      isSubmitting: true, 
      error: null,
      isSuccess: false 
    }));

    try {
      // Submit to Customer.io
      const customerIOSuccess = await customerIO.collectEmail(email, {
        source: options.source,
        feature: options.feature,
        userId: user?.id,
        additionalTraits: {
          user_tier: user?.user_metadata?.tier || 'anonymous',
          authenticated: !!user,
          ...options.additionalTraits,
        },
      });

      // Track analytics (PostHog/other providers)
      if (options.trackAnalytics !== false) {
        trackEvent('email_collected', {
          source: options.source,
          feature: options.feature,
          email_domain: email.split('@')[1],
          user_authenticated: !!user,
          user_tier: user?.user_metadata?.tier || 'anonymous',
          customerio_success: customerIOSuccess,
        });
      }

      if (customerIOSuccess) {
        setState(prev => ({ 
          ...prev, 
          isSubmitting: false,
          isSuccess: true,
          error: null 
        }));
        
        console.log('✅ Email collection successful:', {
          email,
          source: options.source,
          feature: options.feature
        });
        
        return true;
      } else {
        throw new Error('Failed to submit to Customer.io');
      }

    } catch (error) {
      console.error('🚨 Email collection error:', error);
      
      setState(prev => ({ 
        ...prev, 
        isSubmitting: false,
        isSuccess: false,
        error: options.errorMessage || 'Failed to submit email. Please try again.' 
      }));

      // Track error for analytics
      if (options.trackAnalytics !== false) {
        trackEvent('email_collection_error', {
          source: options.source,
          feature: options.feature,
          error: error instanceof Error ? error.message : 'Unknown error',
        });
      }

      return false;
    }
  }, [options, shouldPreventDuplicateEvent, trackEvent, user]);

  const resetState = useCallback(() => {
    setState({
      isSubmitting: false,
      isSuccess: false,
      error: null,
    });
  }, []);

  return {
    submitEmail,
    resetState,
    ...state,
  };
}

// Predefined configurations for common use cases
export const EMAIL_COLLECTION_CONFIGS = {
  AUDIO_MEMORIES: {
    source: 'audio_memories_page',
    feature: 'early_access',
    successMessage: "You're on the list! We'll notify you when audio memories launch.",
    errorMessage: 'Failed to sign up for audio memories notifications.',
  },
  IMAGE_MEMORIES: {
    source: 'image_memories_page', 
    feature: 'early_access',
    successMessage: "You're on the list! We'll notify you when image memories launch.",
    errorMessage: 'Failed to sign up for image memories notifications.',
  },
  VIDEO_MEMORIES: {
    source: 'video_memories_page',
    feature: 'enterprise_demo',
    successMessage: 'Enterprise interest recorded! Our team will contact you.',
    errorMessage: 'Failed to submit enterprise demo request.',
  },
  NEWSLETTER: {
    source: 'newsletter_signup',
    feature: 'newsletter',
    successMessage: 'Successfully subscribed to our newsletter!',
    errorMessage: 'Failed to subscribe to newsletter.',
  },
  COMING_SOON: {
    source: 'coming_soon_modal',
    feature: 'early_access',
    successMessage: "Thanks for your interest! We'll keep you updated.",
    errorMessage: 'Failed to submit your interest.',
  },
} as const;
